import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { IClient } from '@internal-models/client.model';
import { ClientProvider } from '@internal-providers/client.provider';
import { JobProvider } from '@internal-providers/job.provider';
import { Action, NavigationType } from '@shared-libs/enums';
import { translate } from '@shared-libs/helpers';
import { IJob, JobStatus } from '@shared-models/job.model';
import { MessageService } from '@shared-services/message.service';
import { NavigationService } from '@shared-services/navigation.service';
import { ValidationService } from '@shared-services/validation.service';
import { remove } from 'lodash';

@Component({
	selector: 'import-clients-desktop-page',
	templateUrl: 'import-clients.desktop.page.html',
	styleUrls: ['./import-clients.desktop.page.scss'],
})
export class ImportClientsDesktopPage implements OnInit {
	@ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

	public activeStep: number = 1;
	public uploadForm: FormData = new FormData();
	public importJob: IJob = null;
	public getProgressInterval: any;
	public clients: Array<IClient> = [];
	public importErrors: Array<{ excelRow: number; errors: Array<Array<string>> }>;
	public apiError: string;

	public pageDataLoaded: boolean = false;
	public showUploadButton: boolean = false;
	public showUploadPending: boolean = false;
	public showPreview: boolean = false;
	public showError: boolean = false;
	public showSuccess: boolean = false;

	constructor(
		private readonly provider: ClientProvider,
		private readonly jobProvider: JobProvider,
		private readonly messageService: MessageService,
		private readonly navigationService: NavigationService,
		private readonly validationService: ValidationService
	) {}

	public async ngOnInit(): Promise<void> {
		this.navigationService.onNavigateToCurrentPage().subscribe(async () => {
			this.importJob = await this.jobProvider.getLastRunning();
			this.pageDataLoaded = true;
			if (this.importJob) {
				this.handleExcelValidationJobProgress();
			}
		});
	}

	public async downloadImportTemplate(): Promise<void> {
		await this.provider.getImportTemplate();
		this.activeStep = 2;
		this.messageService.showSavedMessage(translate('CLIENTS_IMPORT.TEMPLATE_DOWNLOADED'));
	}

	public async addExcelToForm(event: Event & { target: HTMLInputElement }): Promise<void> {
		this.resetUI();
		const file: File = event.target.files?.[0];
		if (file) {
			this.uploadForm.append('excel', file, 'clients_import.xlsx');
			this.showUploadButton = true;
		}
	}

	public removeSelectedFile(): void {
		if (this.fileInput) {
			this.fileInput.nativeElement.value = null;
		}
		this.uploadForm.delete('excel');
		this.getProgressInterval = null;
	}

	public async startExcelValidationJob(): Promise<void> {
		try {
			this.importJob = await this.provider.startImportClientsValidationAndFormatting(this.uploadForm);
			this.handleExcelValidationJobProgress();
		} catch (error) {
			this.removeSelectedFile();
		}
	}

	public removeClientFromImport(client: IClient): void {
		remove(this.clients, client);
	}

	public async reUploadExcelFile(): Promise<void> {
		this.resetUI();
		this.activeStep = 2;
		await this.jobProvider.deleteOne(this.importJob.id);
	}

	public async importClients(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('IMPORT_CLIENTS')) {
			await this.provider.importClients(this.clients);
			this.resetUI();
			this.showSuccess = true;
			await this.jobProvider.deleteOne(this.importJob.id);
		}
	}

	public async goToClientsPage(): Promise<void> {
		void this.navigationService.navigateTo<{ action: Action }>(NavigationType.page, '/clienten');
		this.resetValues();
	}

	private resetValues(): void {
		this.resetUI();
		this.activeStep = 1;
		this.uploadForm = new FormData();
		this.importJob = null;
		this.getProgressInterval = null;
		this.clients = [];
		this.importErrors = [];
		this.pageDataLoaded = false;
	}

	private resetUI(): void {
		this.showUploadButton = false;
		this.showUploadPending = false;
		this.showPreview = false;
		this.showError = false;
		this.showSuccess = false;
	}

	private handleExcelValidationJobProgress(): void {
		this.activeStep = 3;
		switch (this.importJob.status) {
			case JobStatus.Created:
			case JobStatus.Pending: {
				this.showUploadPending = true;
				if (!this.getProgressInterval) {
					this.startGetProgressInterval();
				}
				break;
			}
			case JobStatus.Finished: {
				clearInterval(this.getProgressInterval);
				this.showUploadPending = false;
				this.showPreview = true;
				this.clients = this.importJob.outputData.validatedAndFormattedClients;
				this.activeStep = 4;
				break;
			}
			case JobStatus.Failed: {
				clearInterval(this.getProgressInterval);
				this.removeSelectedFile();
				this.showUploadPending = false;
				this.showError = true;
				if (this.importJob.outputData.error.response) {
					this.importErrors = this.importJob.outputData.error.response.message;
				} else {
					this.apiError = this.importJob.outputData.error.name;
				}
				break;
			}
		}
	}

	private startGetProgressInterval(): void {
		this.getProgressInterval = setInterval(async () => {
			this.importJob = await this.jobProvider.getOne(this.importJob.id);
			this.handleExcelValidationJobProgress();
		}, 5000);
	}
}
